import * as React from 'react';

/**
 * Size options for the Spinner component
 * - xs: 16px
 * - sm: 24px
 * - md: 32px
 * - lg: 48px
 * - or a custom number value
 */
export type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | number;

/**
 * Base props for the Spinner component
 */
interface SpinnerBaseProps {
  /**
   * The size of the spinner
   * - 'xs': 16px
   * - 'sm': 24px
   * - 'md': 32px (default)
   * - 'lg': 48px
   * - number: custom size in pixels
   * @default 'md'
   */
  size?: SpinnerSize;
  /**
   * The color of the spinner
   * @default 'var(--color-primary-600, #486581)'
   */
  color?: string;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * SpinnerProps extends HTML output attributes
 * Using <output> element for semantic status announcements
 */
export type SpinnerProps = SpinnerBaseProps &
  Omit<React.HTMLAttributes<HTMLOutputElement>, keyof SpinnerBaseProps>;

/**
 * Size values in pixels
 */
const sizeMap: Record<Exclude<SpinnerSize, number>, number> = {
  xs: 16,
  sm: 24,
  md: 32,
  lg: 48,
};

/**
 * Spinner component for displaying loading states.
 *
 * Designed for insurance industry applications where clear loading
 * indicators help users understand system state during data processing.
 *
 * Features:
 * - Four preset sizes (xs, sm, md, lg) plus custom sizes
 * - Configurable color (default primary-600)
 * - 600ms linear infinite rotation animation
 * - Respects prefers-reduced-motion preference
 * - Screen reader text for accessibility
 *
 * @example
 * // Basic usage
 * <Spinner />
 *
 * @example
 * // Different sizes
 * <Spinner size="xs" />
 * <Spinner size="sm" />
 * <Spinner size="md" />
 * <Spinner size="lg" />
 *
 * @example
 * // Custom size
 * <Spinner size={64} />
 *
 * @example
 * // Custom color
 * <Spinner color="#047857" />
 *
 * @example
 * // Within a button (loading state)
 * <Button disabled>
 *   <Spinner size="xs" color="white" className="mr-2" />
 *   Loading...
 * </Button>
 */
const Spinner = React.forwardRef<HTMLOutputElement, SpinnerProps>(
  (
    {
      size = 'md',
      color = 'var(--color-primary-600, #486581)',
      className = '',
      ...props
    },
    ref
  ) => {
    // Determine pixel size
    const pixelSize = typeof size === 'number' ? size : sizeMap[size];

    // Calculate stroke width proportionally (roughly 10% of size, min 2px)
    const strokeWidth = Math.max(2, Math.round(pixelSize * 0.1));

    // Calculate circle radius and circumference
    const radius = (pixelSize - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;

    // Show 75% of the circle (25% gap)
    const dashArray = circumference * 0.75;
    const dashOffset = 0;

    // Combine classes
    const combinedClasses = [
      'inline-flex',
      'items-center',
      'justify-center',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <output
        ref={ref}
        aria-label="Loading"
        className={combinedClasses}
        {...props}
      >
        <svg
          width={pixelSize}
          height={pixelSize}
          viewBox={`0 0 ${pixelSize} ${pixelSize}`}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="animate-spin motion-reduce:animate-none"
          style={{
            animationDuration: '600ms',
            animationTimingFunction: 'linear',
          }}
          aria-hidden="true"
        >
          {/* Background track (optional - subtle indicator of full circle) */}
          <circle
            cx={pixelSize / 2}
            cy={pixelSize / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            style={{
              color: color,
              opacity: 0.2,
            }}
          />
          {/* Animated arc */}
          <circle
            cx={pixelSize / 2}
            cy={pixelSize / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={`${dashArray} ${circumference}`}
            strokeDashoffset={dashOffset}
            style={{
              color: color,
              transformOrigin: 'center',
            }}
          />
        </svg>
        {/* Screen reader text */}
        <span className="sr-only">Loading...</span>
      </output>
    );
  }
);

Spinner.displayName = 'Spinner';

export { Spinner };
